import React from "react";
import { DateTime } from "luxon";
import { TListingValuationTabOutput, TValuationTabComparable } from "../../../../domain/codecs/formIO/ListingValuationTabFormIO";
import { penceToCopyText } from "../../../../shared/src/util";
import { CRMCardOutsideLabelled } from "../CRMCardOutsideLabelled/CRMCardOutsideLabelled";
import { CRMTableHeaderRow } from "../Simple/CRMTableHeaderRow/CRMTableHeaderRow";
import { SpacingColumn } from "../BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../BuildingBlocks/SpacingRow";
import { Background } from "../BuildingBlocks/Background";
import { Text } from "../BuildingBlocks/Text";
import { Padding } from "../BuildingBlocks/Padding";
import { Truncate } from "../BuildingBlocks/Truncate";
import { WeightBold } from "../WeightBold/WeightBold";
import { CRMSpacing } from "../../models/CRMSpacing";
import { CRMColors } from "../../models/CRMColors";
import { CRMFontSizes } from "../../models/CRMFontSizes";
import { propertyClass } from "./valuationTabSteps";

const COLS = "1fr 100px 55px";

const SOURCE_LABELS: Record<TValuationTabComparable["source"], string> = {
    propertydata: "PropertyData",
    rightmove: "Rightmove",
};

const matchPercentage = (relevanceScore: number | null): string =>
    relevanceScore === null ? "—" : `${Math.round(relevanceScore * 100)}%`;

// Rounded to the nearest pound — the raw division carries pence, and "£3,388.89/m²"
// implies a precision the floor area does not have.
const pricePerSqm = (comparable: TValuationTabComparable): string | null => {
    if (comparable.price_pence === null || comparable.floor_area_sqm === null || comparable.floor_area_sqm <= 0) {
        return null;
    }
    return `${penceToCopyText(Math.round(comparable.price_pence / comparable.floor_area_sqm / 100) * 100)}/m²`;
};

const formatSoldDate = (soldDate: string | null): string | null =>
    soldDate === null ? null : DateTime.fromISO(soldDate).setZone("Europe/London").toFormat("MMM yyyy");

const mismatchNote = (
    comparable: TValuationTabComparable,
    subjectPropertyType: string | null,
): string | null => {
    const subject = propertyClass(subjectPropertyType);
    const comp = propertyClass(comparable.property_type);
    if (subject === "unknown" || comp === "unknown" || subject === comp) {
        return null;
    }
    return comp === "flat" ? "a flat, not a house" : "a house, not a flat";
};

const ComparableRow = (props: {
    comparable: TValuationTabComparable;
    subjectPropertyType: string | null;
}): JSX.Element => {
    const { comparable } = props;
    const mismatch = mismatchNote(comparable, props.subjectPropertyType);

    return (
        <SpacingRow spacing={CRMSpacing.SMALL} childSize={COLS} alignItems="center">
            <SpacingColumn spacing={CRMSpacing.TINY}>
                <Truncate>
                    <Text size={CRMFontSizes.SMALL}>{comparable.address ?? comparable.postcode ?? "Unknown address"}</Text>
                </Truncate>
                <Text size={CRMFontSizes.X_SMALL} color={mismatch === null ? CRMColors.NEUTRAL_4 : CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0}>
                    {[
                        comparable.property_type,
                        comparable.bedrooms === null ? null : `${comparable.bedrooms} bed`,
                        comparable.distance_km === null ? null : `${comparable.distance_km.toFixed(1)} km away`,
                        pricePerSqm(comparable),
                        formatSoldDate(comparable.sold_date),
                        mismatch,
                    ].filter((part) => part !== null).join(" · ")}
                </Text>
            </SpacingColumn>
            <Text size={CRMFontSizes.SMALL} align="right">
                {comparable.price_pence === null
                    ? "—"
                    : <WeightBold>{penceToCopyText(comparable.price_pence)}</WeightBold>
                }
            </Text>
            <Text size={CRMFontSizes.X_SMALL} align="right"><WeightBold>{matchPercentage(comparable.relevance_score)}</WeightBold></Text>
        </SpacingRow>
    );
};

export const CRMValuationComparablesCard = (props: { output: TListingValuationTabOutput }): JSX.Element => {
    const { output } = props;

    // Where these came from and when. Without it, the comps sitting next to a failed
    // fetch read as broken data — four testers stalled here and one said the first
    // question they always get asked is "where did these come from?".
    const sources = Array.from(new Set(output.comparables.map((c) => SOURCE_LABELS[c.source])));
    const provenance = output.comps_fetched_at === null || sources.length === 0
        ? null
        : `From ${sources.join(" and ")}, collected ${DateTime.fromISO(output.comps_fetched_at).setZone("Europe/London").toFormat("d MMM")}`;

    return (
        <CRMCardOutsideLabelled labelColor="highlights-calming-pink-2">
            <Padding spacing={CRMSpacing.MEDIUM}>
                <SpacingColumn spacing={CRMSpacing.SMALL}>
                    <SpacingColumn spacing={CRMSpacing.TINY}>
                        <Text size={CRMFontSizes.MED_PLUS}><WeightBold>Sales we compared against</WeightBold></Text>
                        {provenance !== null &&
                            <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_4}>{provenance}</Text>
                        }
                    </SpacingColumn>

                    {output.comparables.length === 0 &&
                        <Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_4}>
                            No comparable sales collected yet.
                        </Text>
                    }

                    {output.comparables.length > 0 &&
                        <SpacingColumn spacing={CRMSpacing.TINY}>
                            <CRMTableHeaderRow
                                spacing={CRMSpacing.SMALL}
                                spacingRowChildSize={COLS}
                                headers={["Address", "Sold for", "Match"]}
                            />
                            {output.comparables.map((comparable, index) =>
                                <Background
                                    key={`${comparable.source}-${comparable.address ?? ""}-${index}`}
                                    color={index % 2 === 0 ? CRMColors.NEUTRAL_PAPER : CRMColors.NEUTRAL_12}
                                    padding={`${CRMSpacing.TINY} ${CRMSpacing.SMALL}`}
                                    borderRadius="3px"
                                >
                                    <ComparableRow
                                        comparable={comparable}
                                        subjectPropertyType={output.subject_property_type}
                                    />
                                </Background>,
                            )}
                            <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_4}>
                                Match is how closely each sale resembles this property, on type,
                                size, distance and how recent the sale was.
                            </Text>
                        </SpacingColumn>
                    }
                </SpacingColumn>
            </Padding>
        </CRMCardOutsideLabelled>
    );
};
